import { z } from 'zod/v4';
import { moduleItemTypeEnum } from '../enums';
import { createCourseSchema } from './courses';
import { createSubscriptionPlanSchema } from './subscription-plans';
import { createChannelPassSchema } from './channel-passes';

// ============================================
// Draft Schemas (без FK полей — они подставляются автоматически)
// ============================================

/**
 * Тариф без привязки к сущности (channelPassId/courseId/subscriptionPlanId подставляются на backend)
 */
export const pricingTierDraftSchema = z.object({
  durationDays: z.number().int().positive().nullish(),
  prices: z
    .record(z.string().length(3), z.number().min(0))
    .refine((p) => Object.keys(p).length > 0, {
      message: 'At least one price is required',
    }),
  baseCurrency: z.string().length(3).default('UZS'),
  isActive: z.boolean().default(true),
  order: z.number().int().min(0).default(0),
  isFamily: z.boolean().default(false),
  familySlots: z.number().int().min(0).default(0),
  imageId: z.number().int().positive().nullable().optional(),
  gradientFrom: z.string().nullable().optional(),
  gradientTo: z.string().nullable().optional(),
  gradientAngle: z.number().int().min(0).max(360).optional(),
  cardAppearance: z
    .object({
      light: z.object({
        bg: z.string(),
        patternPrimary: z.string(),
        patternSecondary: z.string(),
      }),
      dark: z.object({
        bg: z.string(),
        patternPrimary: z.string(),
        patternSecondary: z.string(),
      }),
    })
    .nullable()
    .optional(),
  targetLanguages: z.array(z.string().min(2).max(5)).default([]),
  translations: z
    .array(
      z.object({
        languageCode: z.string().min(2).max(5),
        name: z.string().min(1).max(200),
        shortDescription: z.string().max(500).nullish(),
      }),
    )
    .optional(),
  bonusTemplates: z
    .array(
      z.object({
        type: z.enum(['NUMBER', 'BOOLEAN', 'ARRAY', 'STRING']),
        initialValue: z.union([
          z.number().min(0),
          z.boolean(),
          z.array(z.string()),
          z.string(),
        ]),
        durationMode: z.enum(['RELATIVE', 'ABSOLUTE', 'INHERIT']).default('INHERIT'),
        durationDays: z.number().int().positive().nullish(),
        expiresAt: z.string().datetime().nullish(),
        order: z.number().int().min(0).default(0),
        translations: z
          .array(
            z.object({
              languageCode: z.string().min(2).max(5),
              name: z.string().min(1).max(200),
              description: z.string().max(1000).nullish(),
            }),
          )
          .optional(),
      }),
    )
    .optional(),
});

/**
 * Элемент модуля без moduleId (подставляется при создании)
 */
export const moduleItemDraftSchema = z.object({
  type: moduleItemTypeEnum,
  isPreview: z.boolean().default(false),
  videoUrl: z.string().url().nullable().optional(),
  videoDuration: z.number().int().positive().nullable().optional(),
  bookId: z.number().int().positive().nullable().optional(),
  translations: z
    .array(
      z.object({
        languageCode: z.string().min(2).max(5),
        title: z.string().min(1).max(200),
        content: z.string().max(50000).nullable().optional(),
      }),
    )
    .min(1),
});

/**
 * Модуль без courseId (подставляется при создании)
 */
export const moduleDraftSchema = z.object({
  translations: z
    .array(
      z.object({
        languageCode: z.string().min(2).max(5),
        title: z.string().min(1).max(200),
        description: z.string().max(2000).nullable().optional(),
      }),
    )
    .min(1),
  items: z.array(moduleItemDraftSchema).optional(),
});

// ============================================
// Composite Schemas
// ============================================

/**
 * Создание курса + тарифы + модули одним запросом
 */
export const createCourseCompositeSchema = createCourseSchema.extend({
  pricingTiers: z.array(pricingTierDraftSchema).optional(),
  modules: z.array(moduleDraftSchema).optional(),
});

/**
 * Создание абонемента + тарифы одним запросом
 */
export const createSubscriptionPlanCompositeSchema = createSubscriptionPlanSchema.extend({
  pricingTiers: z.array(pricingTierDraftSchema).optional(),
});

/**
 * Создание доступа в канал + тарифы одним запросом
 */
export const createChannelPassCompositeSchema = createChannelPassSchema.extend({
  pricingTiers: z.array(pricingTierDraftSchema).optional(),
});

// ============================================
// Type Exports
// ============================================

export type CreateCourseCompositeInput = z.infer<typeof createCourseCompositeSchema>;
export type CreateSubscriptionPlanCompositeInput = z.infer<typeof createSubscriptionPlanCompositeSchema>;
export type CreateChannelPassCompositeInput = z.infer<typeof createChannelPassCompositeSchema>;
export type PricingTierDraftInput = z.infer<typeof pricingTierDraftSchema>;
export type ModuleDraftInput = z.infer<typeof moduleDraftSchema>;
export type ModuleItemDraftInput = z.infer<typeof moduleItemDraftSchema>;
